import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Title, Select, Button, Paper, Stack, Text, Box, Group, Checkbox, Textarea } from '@mantine/core';
import { IconFileReport, IconDownload, IconUser, IconCalendar, IconBuilding, IconMail } from '@tabler/icons-react';
import { ReportService } from './service';
import { AuditWithExpand, ReportOptions, PageOrientation } from './types';

const DEFAULT_EMAIL_TEMPLATE = `Betreff: 
Auditbericht ASGS {{direktion}} {{dienststelle}} {{datum}}

Sehr geehrte Damen und Herren

Herzlichen Dank für den freundlichen Empfang zum Audit vom {{datum}} und Ihr Engagement für ASGS.

In der Beilage erhalten Sie den Auditbericht mit entsprechenden Massnahmenvorschlägen. 
Zudem habe ich folgende Dokumente angefügt:
-	Vorlage Aufgabenbeschrieb SIBE
-	Vorlage Ausbildungsnachweis
-	Vorlage Wartungsprotokoll Leiter
-	Checkliste: Neue Mitarbeitende
-	Checkliste Gefährdungsermittlung Büro

Wenn sich nach der Durchsicht des Berichtes Fragen ergeben, können Sie sich gerne an uns wenden.

Ich wünsche Ihnen einen schönen Tag.

[Signatur]
`;

const Berichte: React.FC = () => {
  const location = useLocation();
  const [audits, setAudits] = useState<AuditWithExpand[]>([]);
  const [selectedAudit, setSelectedAudit] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [, setError] = useState<string | null>(null);
  const defaultStandardText = `Einleitung

Zum Audit wurde ich von "Begleitperson" empfangen. Seit dem letzten Audit vom XXXX konnten einige der vorgeschlagenen Massnahmen umgesetzt werden.

Die noch offenen Punkte werden der Vollständigkeit halber nachfolgend nochmals aufgeführt. Wir empfehlen, für die Umsetzung der Massnahmen und Empfehlungen die Zuständigkeiten (Person gemäss Sicherheitsorganisation) und Termine zu definieren.`;

  const [standardText, setStandardText] = useState<string>(defaultStandardText);
  const [emailTemplate, setEmailTemplate] = useState<string>(DEFAULT_EMAIL_TEMPLATE);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const settings = await ReportService.fetchSettings?.();
        if (settings?.emailTemplate) {
          setEmailTemplate(settings.emailTemplate);
        }
      } catch (err) {
        console.warn("Konnte E-Mail Vorlage aus DB nicht laden", err);
      }
    };
    fetchTemplate();
  }, []);

  // const saveTemplateToDB = async () => {
  //   try {
  //     await ReportService.updateSettingsEmailTemplate(emailTemplate);
  //     alert("E-Mail Vorlage gespeichert.");
  //   } catch (err) {
  //     console.error("Fehler beim Speichern der Vorlage", err);
  //     alert("Fehler beim Speichern der Vorlage.");
  //   }
  // };

  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    showIstzustand: true,
    showMassnahme: true,
    showBemerkung: false,
    showAnhange: true,
    showDatum: true,
    showAuditoren: true,
    showDienststelle: true,
    showAdresse: true,
    showSicherheitssysteme: true,
    showBegleitpersonen: true,
    orientation: 'landscape' as PageOrientation
  });

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initializeComponent = async () => {
      const { pb } = await import('../../lib/pocketbase');

      // ✅ Reagiere auf spätere Login-Änderungen
      unsubscribe = pb.authStore.onChange(async () => {
        if (!pb.authStore.isValid) return;

        try {
          setLoading(true);
          const fetchedAudits = await ReportService.fetchAudits();
          setAudits(fetchedAudits);
          const params = new URLSearchParams(location.search);
          const preSelectedAuditId = params.get('audit');
          if (preSelectedAuditId) {
            setSelectedAudit(preSelectedAuditId);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten';
          setError(errorMessage);
          console.error('Initialization error:', err);
        } finally {
          setLoading(false);
        }
      }, true); // true = sofort beim aktuellen Auth-State ausführen
    };

    initializeComponent();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [location]);

  const handleGenerateReport = async (auditId: string) => {
    if (!auditId) return;
    setLoading(true);
    setError(null);
    try {
      await ReportService.generateReport(auditId, { ...reportOptions, standardText });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten';
      setError(errorMessage);
      console.error('Report generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEmail = async (audit: AuditWithExpand) => {
    try {
      setLoading(true);
      setError(null);
      const formattedDate = new Date(audit.auditdatum).toLocaleDateString('de-CH');
      const auditors = audit.expand?.auditor || [];
      const auditorNames = auditors.map(a => `${a.vorname} ${a.name}`);
      const primaryAuditor = auditorNames[0] || 'Nicht verfügbar';
      const kontrollpunkte = audit.expand?.kontrollpunkt || [];
      const kontrollpunkteNames = kontrollpunkte.map(k => k.sicherheitssystem).join(', ');
      const dienststelleName = audit.expand?.dienststelle?.name || 'Nicht verfügbar';
      const begleitpersonenText = audit.begleitpersonen ? `Begleitet wurde der Audit von: ${audit.begleitpersonen}.` : '';
      const { pb } = await import('../../lib/pocketbase');
      const signatureName = pb.authStore.model?.name?.trim() || '[Signatur]';

      let populatedTemplate = emailTemplate
        .replace(/{{auditnummer}}/g, audit.auditnummer.toString())
        .replace(/{{datum}}/g, formattedDate)
        .replace(/{{dienststelle}}/g, dienststelleName)
        .replace(/{{direktion}}/g, audit.expand?.dienststelle?.expand?.direktion?.name || '')
        .replace(/{{auditor}}/g, primaryAuditor)
        .replace(/{{auditors}}/g, auditorNames.join(', '))
        .replace(/{{kontrollpunkte}}/g, kontrollpunkteNames)
        .replace(/{{begleitpersonen}}/g, begleitpersonenText)
        .replace(/\[Signatur\]/g, signatureName);
      const subjectMatch = populatedTemplate.match(/Betreff:\s*(.*)/);
      const subject = subjectMatch ? encodeURIComponent(subjectMatch[1].trim()) : '';
      const bodyStartIndex = subjectMatch ? populatedTemplate.indexOf('\n', populatedTemplate.indexOf(subjectMatch[0])) + 1 : 0;
      const bodyText = populatedTemplate.substring(bodyStartIndex).trim();
      const body = encodeURIComponent(bodyText);
      const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
    } catch (err) {
      console.error('Error opening email:', err);
      setError('Fehler beim Öffnen der E-Mail');
    } finally {
      setLoading(false);
    }
  };

  const selectedAuditInfo = audits.find(audit => audit.id === selectedAudit);

  // Fallback: ensure dienststelle name is resolved even if expand gets lost
  // const getDienststelleName = (audit?: AuditWithExpand) => {
  //   if (!audit) return 'Nicht verfügbar';
  //   if (audit.expand?.dienststelle?.name) return audit.expand.dienststelle.name;

  //   // try to resolve from audits list (in case expand was dropped)
  //   const fullAudit = audits.find(a => a.id === audit.id);
  //   return fullAudit?.expand?.dienststelle?.name || 'Nicht verfügbar';
  // };

  return (
    <Container size="md" px="xs">
        <Paper shadow="xs" p={{ base: 'md', sm: 'xl' }} mt="md">
          <Stack gap="md">
            <Group>
              <IconFileReport size={24} />
              <Title order={2}>Berichte ({audits.length})</Title>
            </Group>
            <Select
              label="Audit auswählen"
              placeholder="Bitte wählen..."
              data={audits.map((audit) => ({
                value: audit.id,
                label: `📋 Audit #${audit.auditnummer} - ${new Date(audit.auditdatum).toLocaleDateString('de-CH')}\n${audit.expand?.dienststelle?.name || 'Keine Dienststelle'}${audit.begleitpersonen ? ` • ${audit.begleitpersonen}` : ''}`
              }))}
              value={selectedAudit}
              onChange={(value) => setSelectedAudit(value || '')}
              disabled={loading}
              size="md"
              w="100%"
              styles={{
                input: {
                  whiteSpace: 'pre-line',
                  minHeight: '60px'
                }
              }}
            />
            {selectedAuditInfo && (
              <>
                {(() => {
                  const begleitPerson = selectedAuditInfo.begleitpersonen?.trim();
                  if (begleitPerson) {
                    const newText = defaultStandardText.replace(/"Begleitperson"/g, begleitPerson);
                    if (standardText === defaultStandardText) {
                      setStandardText(newText);
                    }
                  }
                })()}
                <Box p="md" bg="gray.0" style={{ borderRadius: '8px' }}>
                  <Stack gap="xs">
                    <Group>
                      <IconUser size={18} />
                      <Text size="sm">
                        Zuständig (SIBE): {selectedAuditInfo.expand?.auditor?.length
                          ? selectedAuditInfo.expand.auditor.map(a => a.name?.trim() || a.email?.trim() || 'Unbekannt').filter(Boolean).join(', ')
                          : 'Nicht verfügbar'}
                      </Text>
                    </Group>
                    <Group>
                      <IconCalendar size={18} />
                      <Text size="sm">Datum: {new Date(selectedAuditInfo.auditdatum).toLocaleDateString('de-CH')}</Text>
                    </Group>
                    <Group>
                      <IconBuilding size={18} />
                      <Text size="sm">Dienststelle: {selectedAuditInfo.expand?.dienststelle?.name || 'Nicht verfügbar'}</Text>
                    </Group>
                  </Stack>
                </Box>
                <Paper withBorder p="md">
                  <Stack gap="md">
                    <Group mb="xs">
                      <IconFileReport size={18} />
                      <Text fw={500}>Berichtsoptionen</Text>
                    </Group>
                    <Box>
                      <Text size="sm" fw={500} mb="xs">Standardtext</Text>
                      <Textarea
                        value={standardText}
                        onChange={(e) => setStandardText(e.currentTarget.value)}
                        minRows={8}
                        maxRows={8}
                        autosize={false}
                        styles={{ input: { fontSize: '0.95rem' } }}
                      />
                    </Box>
                    <Box>
                      <Text size="sm" fw={500} mb="xs">Audit-Informationen</Text>
                      <Stack gap="xs">
                        <Checkbox label="Datum" checked={reportOptions.showDatum} onChange={(e) => setReportOptions(prev => ({ ...prev, showDatum: e.currentTarget.checked }))} />
                        <Checkbox label="Auditoren" checked={reportOptions.showAuditoren} onChange={(e) => setReportOptions(prev => ({ ...prev, showAuditoren: e.currentTarget.checked }))} />
                        <Checkbox label="Dienststelle" checked={reportOptions.showDienststelle} onChange={(e) => setReportOptions(prev => ({ ...prev, showDienststelle: e.currentTarget.checked }))} />
                        <Checkbox label="Adresse" checked={reportOptions.showAdresse} onChange={(e) => setReportOptions(prev => ({ ...prev, showAdresse: e.currentTarget.checked }))} />
                        <Checkbox label="Kontrollpunkte" checked={reportOptions.showSicherheitssysteme} onChange={(e) => setReportOptions(prev => ({ ...prev, showSicherheitssysteme: e.currentTarget.checked }))} />
                        <Checkbox label="Begleitpersonen" checked={reportOptions.showBegleitpersonen} onChange={(e) => setReportOptions(prev => ({ ...prev, showBegleitpersonen: e.currentTarget.checked }))} />
                      </Stack>
                    </Box>
                    <Box>
                      <Text size="sm" fw={500} mb="xs">Massnahmen-Details</Text>
                      <Stack gap="xs">
                        <Checkbox label="Ist-Zustand" checked={reportOptions.showIstzustand} onChange={(e) => setReportOptions(prev => ({ ...prev, showIstzustand: e.currentTarget.checked }))} />
                        <Checkbox label="Massnahme" checked={reportOptions.showMassnahme} onChange={(e) => setReportOptions(prev => ({ ...prev, showMassnahme: e.currentTarget.checked }))} />
                        <Checkbox label="Bemerkungen" checked={reportOptions.showBemerkung} onChange={(e) => setReportOptions(prev => ({ ...prev, showBemerkung: e.currentTarget.checked }))} />
                        <Checkbox label="Anhänge" checked={reportOptions.showAnhange} onChange={(e) => setReportOptions(prev => ({ ...prev, showAnhange: e.currentTarget.checked }))} />
                      </Stack>
                    </Box>
                    <Button
                      onClick={() => handleGenerateReport(selectedAudit)}
                      loading={loading}
                      disabled={!selectedAudit || loading}
                      size="md"
                      leftSection={<IconDownload size={20} />}
                      fullWidth
                    >
                      {loading ? 'Generiere Bericht...' : 'Bericht generieren'}
                    </Button>
                  </Stack>
                </Paper>
                <Paper withBorder p="md">
                  <Stack gap="md">
                    <Group mb="xs">
                      <IconMail size={18} />
                      <Text fw={500}>E-Mail Optionen</Text>
                    </Group>
                    <Box>
                      <Text size="sm" fw={500} mb="xs">E-Mail Vorlage</Text>
                      <Text size="xs" c="dimmed" mb="xs">
                        Verfügbare Variablen: {`{{auditnummer}}`}, {`{{datum}}`}, {`{{dienststelle}}`}, {`{{auditor}}`}, {`{{auditors}}`}, {`{{kontrollpunkte}}`}, {`{{begleitpersonen}}`}
                      </Text>
                      <Textarea
                        value={emailTemplate}
                        onChange={(e) => setEmailTemplate(e.currentTarget.value)}
                        minRows={15}
                        maxRows={30}
                        autosize
                        styles={{ input: { fontFamily: 'monospace' } }}
                        placeholder="E-Mail Vorlage mit Variablen"
                      />
                    </Box>
                    <Button
                      onClick={() => selectedAuditInfo && handleOpenEmail(selectedAuditInfo)}
                      disabled={!selectedAudit || loading}
                      size="md"
                      leftSection={<IconMail size={20} />}
                      fullWidth
                      variant="light"
                    >
                      {loading ? 'Öffne E-Mail...' : 'E-Mail öffnen'}
                    </Button>
                  </Stack>
                </Paper>
              </>
            )}
          </Stack>
        </Paper>
      </Container>
  );
};

export default Berichte;
